import { find, values } from "lodash";
import React, { useMemo } from "react";
import { useAuthentication } from "../context/authentication";
import {
  DottedMenu,
  MenuItem
} from "../context/dropdownMenu/DropdownMenuProvider";
import { useHotspots } from "../context/hotspots";
import { useMembers } from "../context/members";
import { useToast } from "../context/toast";
import { useTransmitScales } from "../context/transmitScales";
import { useWallets } from "../context/wallets";
import { useWorkspaces } from "../context/workspaces/WorkspacesProvider";
import { Hotspot } from "../utils/types";
import useTransmitScaleConfig from "../utils/useTransmitScaleConfig";

interface Props {
  disabled?: boolean;
  hotspot: Hotspot;
  toggleAddInstallForm: () => void;
}

const HotspotActionsButton: React.FC<Props> = ({
  disabled,
  hotspot,
  toggleAddInstallForm
}) => {
  const { setModalWarningConfig } = useToast();
  const { wrapActionWithAuth } = useAuthentication();
  const { isTransmitScaleActive } = useTransmitScales();
  const toggleIsActive = useTransmitScaleConfig(
    (state) => state.toggleIsActive
  );
  const { wallets, toggleAddWalletSidebar, removeWallet } = useWallets();
  const {
    workspaceHotspots,
    toggleAddHotspotSidebar,
    removeWorkspaceHotspot,
    walletHotspotCountMap
  } = useHotspots();
  const { membersLimitExceeded } = useMembers();
  const { currentWorkspaceIsLocked } = useWorkspaces();

  const walletHotspotCount = useMemo(
    () => walletHotspotCountMap[hotspot.owner] || 0,
    [walletHotspotCountMap, hotspot.owner]
  );

  const workspaceHotspotWallet = useMemo(
    () =>
      find(values(wallets), {
        address: hotspot.owner
      }),
    [hotspot.owner, wallets]
  );

  const hotspotIsInWorkspace = useMemo(
    () =>
      !!find(values(workspaceHotspots), {
        id: hotspot.id
      }),
    [hotspot.id, workspaceHotspots]
  );

  const menuItems = useMemo(() => {
    const items: MenuItem[] = [];

    if (
      hotspotIsInWorkspace &&
      !currentWorkspaceIsLocked &&
      !membersLimitExceeded
    ) {
      items.push({
        text: "Add install for hotspot",
        onClick: wrapActionWithAuth(toggleAddInstallForm)
      });

      if (workspaceHotspotWallet?.tracked) {
        items.push({
          text: "Remove wallet from workspace",
          onClick: wrapActionWithAuth(() => {
            setModalWarningConfig({
              open: true,
              resolve: () => {
                removeWallet(workspaceHotspotWallet);
              }
            });
          })
        });
      } else {
        items.push({
          text: "Remove hotspot from workspace",
          onClick: wrapActionWithAuth(() => {
            removeWorkspaceHotspot(hotspot.id);
          })
        });
      }
    } else {
      items.push({
        text: "Add hotspot to workspace",
        onClick: wrapActionWithAuth(() => {
          toggleAddHotspotSidebar(hotspot);
        })
      });
      items.push({
        text: `Add ${
          walletHotspotCount > 0 ? walletHotspotCount : ""
        } wallet hotspot${walletHotspotCount <= 1 ? "" : "s"} to workspace`,
        onClick: wrapActionWithAuth(() => {
          toggleAddWalletSidebar(hotspot);
        })
      });
      items.push({
        text: "Message owner",
        to: `/hotspots/${hotspot.id}/owner`
      });
    }

    return items;
  }, [
    hotspotIsInWorkspace,
    currentWorkspaceIsLocked,
    membersLimitExceeded,
    wrapActionWithAuth,
    toggleAddInstallForm,
    workspaceHotspotWallet,
    setModalWarningConfig,
    removeWallet,
    removeWorkspaceHotspot,
    hotspot,
    walletHotspotCount,
    toggleAddHotspotSidebar,
    toggleAddWalletSidebar
  ]);

  const transmitScaleItem = useMemo(() => {
    return {
      text: `${
        isTransmitScaleActive(hotspot.id) ? "Disable " : "Enable "
      } transmit scale`,
      onClick: () => {
        toggleIsActive(hotspot.id);
      }
    };
  }, [hotspot.id, isTransmitScaleActive, toggleIsActive]);

  return (
    <DottedMenu
      disabled={disabled}
      className="z-10"
      text="Open hotspot actions"
      menuItems={menuItems.concat(transmitScaleItem)}
    />
  );
};

export default HotspotActionsButton;
